import { CancellablePromise, isComputedSchema, Path } from "@kform/core";
import * as React from "react";

import { equals } from "../utils/equals";
import { ignorePromiseCancellationException } from "../utils/ignorePromiseCancellationException";
import { DistributedOmit } from "../utils/typeUtils";
import { useLatestValues } from "../utils/useLatestValues";
import {
  FormatterController,
  FormatterControllerState,
  FormatterOptions,
  useFormatter,
} from "./useFormatter";

/**
 * Options available to the {@link useInput} hook.
 */
export type InputOptions<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> = DistributedOmit<
  FormatterOptions<T, TFormatted>,
  "setFormattedValue" | "format"
> &
  InputOwnOptions<T, TFormatted, TInput>;

/**
 * Own options available to the {@link useInput} hook.
 */
export interface InputOwnOptions<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> {
  /**
   * Function used to set the formatted value in the input element.
   *
   * The passed value results from calling {@link format} on the form manager
   * value.
   * If {@link format} is not defined, then the form manager value is passed
   * directly.
   * @param formattedValue Formatted value to set.
   * @param controller Controller of the form value.
   * @param input Input element.
   */
  setFormattedValue?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => void;
  /**
   * Function that formats the form manager value into a value to be set in the
   * input element. The passed {@link value} is `undefined` when the form
   * manager value does not exist.
   * @param value Form manager value to transform.
   * @param controller Controller of the value to transform.
   * @param input Input element.
   * @returns Transformed value.
   */
  format?: (
    value: T | undefined,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => TFormatted | PromiseLike<TFormatted>;
  /**
   * Function used to parse the input element value into a value to be set in
   * the form manager.
   * @param formattedValue Input element value to transform.
   * @param controller Controller of the value to transform.
   * @param input Input element.
   * @returns Transformed value.
   */
  parse?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => T | PromiseLike<T>;
}

/**
 * Controller used to read and manipulate the form value associated with the
 * form input, exposing properties that should be set on the input itself.
 */
export type InputController<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> = FormatterController<T, TFormatted> & InputOwnController<TFormatted, TInput>;

/**
 * Input own controller.
 */
export interface InputOwnController<TFormatted = unknown, TInput = unknown> {
  /**
   * Properties to be passed to the input element.
   */
  inputProps: InputElementProps<TFormatted, TInput>;
}

/**
 * Properties to provide to an input element to integrate it with a form
 * manager.
 */
export interface InputElementProps<TFormatted = unknown, TInput = unknown> {
  /**
   * Name of the input element.
   */
  name: string;
  /**
   * Whether the input element should be read-only.
   *
   * This is `true` when the schema associated with this input is computed.
   */
  readOnly: boolean;
  /**
   * Reference to the input element.
   */
  ref: React.Ref<TInput>;
  /**
   * Function to run whenever the value of the input element changes. This
   * function can receive either a change event with a standard HTML input
   * ({@link HTMLInputElement <input>}, {@link HTMLSelectElement <select>},
   * {@link HTMLTextAreaElement <textarea>}) as target or a value directly.
   *
   * This function sets the new value in the form manager and marks the value as
   * dirty.
   * @param eventOrValue Either a "change event" or the new value.
   */
  onChange: (eventOrValue: React.ChangeEvent | TFormatted) => void;
  /**
   * Function to run whenever the input element is blurred. This function marks
   * the form value as "touched").
   */
  onBlur: () => void;
}

/**
 * Hook used to control the integration between a value of the form manager and
 * an input of the form. It provides access to the form value, as well as
 * properties for integration with an input element.
 * @param path Path of the form value to access, relative to the current path.
 * @param options Available options.
 * @returns A controller used to read and control the form value associated with
 * the form input, exposing properties that should be set on the input itself.
 */
export function useInput<T = unknown, TInput = unknown>(
  path?: Path | string,
  options?: undefined,
): InputController<T, T | undefined, TInput>;
export function useInput<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
>(
  path: Path | string | undefined,
  options: InputOptions<T, TFormatted, TInput>,
): InputController<T, TFormatted, TInput>;
export function useInput<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
>(
  path: Path | string = Path.CURRENT,
  {
    setFormattedValue = defaultSetFormattedValue,
    format,
    parse,
    ...options
  }: InputOptions<T, TFormatted, TInput> = {},
): InputController<T, TFormatted, TInput> {
  const latestOptions = useLatestValues({ parse, setFormattedValue });

  const inputRef = React.useRef<TInput>(null);
  const activeSetPromiseRef = React.useRef<CancellablePromise<void> | null>(
    null,
  );

  // Formatter options
  const formatterSetFormattedValue = React.useCallback(
    (
      formattedValue: TFormatted,
      state: FormatterControllerState<T, TFormatted>,
    ) => {
      // Only set the value if we're not currently setting it
      if (activeSetPromiseRef.current == null) {
        setFormattedValue(formattedValue as never, state, inputRef.current);
      }
    },
    [setFormattedValue],
  );
  const formatterFormat = React.useCallback(
    (value: T | undefined, state: FormatterControllerState<T, TFormatted>) =>
      format ? format(value, state, inputRef.current) : (value as TFormatted),
    [format],
  );

  const controller = useFormatter(path, {
    setFormattedValue: formatterSetFormattedValue,
    format: formatterFormat,
    ...options,
  });

  const handleChange = React.useCallback(
    async (eventOrValue: React.ChangeEvent | TFormatted) => {
      const {
        schema,
        observingDescendants,
        initialized,
        exists,
        dirty,
        formattedValue,
      } = controller.getState();
      if (initialized && exists && !isComputedSchema(schema)) {
        const newValue = isChangeEvent(eventOrValue)
          ? (getEventValue(eventOrValue.target, eventOrValue) as TFormatted)
          : eventOrValue;
        const parsedValue = latestOptions.parse
          ? await latestOptions.parse(
              newValue as never,
              controller.getState(),
              inputRef.current,
            )
          : (newValue as unknown as T);
        if (!equals(formattedValue, parsedValue)) {
          // Cancel previous `set`
          activeSetPromiseRef.current?.cancel("New value is being set.");
          const newSetPromise = (activeSetPromiseRef.current = controller
            .set(parsedValue)
            .catch(ignorePromiseCancellationException)
            .finally(() => {
              if (activeSetPromiseRef.current === newSetPromise) {
                activeSetPromiseRef.current = null;
              }
            }));
          if (!dirty) {
            void controller.setDirty(
              observingDescendants ? Path.CURRENT_DEEP : Path.CURRENT,
            );
          }
        }
      }
    },
    [controller, latestOptions],
  );
  const handleBlur = React.useCallback(() => {
    const {
      observingDescendants,
      initialized,
      exists,
      touched,
      formattedValue,
    } = controller.getState();
    if (initialized && exists) {
      if (!touched) {
        void controller.setTouched(
          observingDescendants ? Path.CURRENT_DEEP : Path.CURRENT,
        );
      }

      if (activeSetPromiseRef.current == null) {
        latestOptions.setFormattedValue(
          formattedValue,
          controller.getState(),
          inputRef.current,
        );
      } else {
        activeSetPromiseRef.current = null;
      }
    }
  }, [controller, latestOptions]);

  // Listeners for changes coming from the input
  const listeners = useLatestValues({
    onChange: handleChange,
    onBlur: handleBlur,
  });

  const inputName = controller.useState((state) => state.path.toString());
  const isComputed = controller.useState((state) =>
    isComputedSchema(state.schema),
  );
  const inputProps = React.useMemo(
    () => ({
      name: inputName,
      readOnly: isComputed,
      ref: inputRef,
      onChange: (eventOrValue: React.ChangeEvent | TFormatted) =>
        listeners.onChange(eventOrValue),
      onBlur: () => listeners.onBlur(),
    }),
    [inputName, isComputed, listeners],
  );

  return React.useMemo(
    () => ({ ...controller, inputProps }),
    [controller, inputProps],
  );
}

/**
 * Default function used to set a formatted value within an input element.
 */
export function defaultSetFormattedValue(
  formattedValue: unknown,
  _state: any,
  input: unknown,
) {
  if (input == null) {
    return;
  }

  if (input instanceof HTMLInputElement && input.type === "checkbox") {
    const oldInputValue = input.checked;
    const newInputValue = !!formattedValue;
    if (oldInputValue !== newInputValue) {
      input.checked = newInputValue;
    }
  } else if (input instanceof HTMLInputElement && input.type === "file") {
    if (formattedValue instanceof FileList) {
      input.files = formattedValue;
    } else {
      input.value = "";
    }
  } else if (
    input instanceof HTMLInputElement ||
    input instanceof HTMLSelectElement ||
    input instanceof HTMLTextAreaElement
  ) {
    const oldInputValue = input.value;
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const newInputValue = String(formattedValue ?? "");
    if (oldInputValue !== newInputValue) {
      input.value = newInputValue;
    }
  } else {
    throw new Error(
      "Unknown input element, please provide a custom `setFormattedValue` " +
        "function.",
    );
  }
}

/**
 * Whether a given value is a change event.
 */
function isChangeEvent(value: unknown): value is React.ChangeEvent {
  return (
    value !== null &&
    typeof value === "object" &&
    "target" in value &&
    value.target !== null &&
    typeof value.target === "object"
  );
}

/**
 * Obtains the value of an element given the event's target.
 */
function getEventValue(
  eventTarget: EventTarget,
  defaultValue: unknown,
): unknown {
  return eventTarget instanceof HTMLInputElement ||
    eventTarget instanceof HTMLTextAreaElement ||
    eventTarget instanceof HTMLSelectElement
    ? eventTarget.type === "checkbox"
      ? (eventTarget as HTMLInputElement).checked
      : eventTarget.type === "file"
        ? (eventTarget as HTMLInputElement).files
        : eventTarget.value
    : defaultValue;
}
